import { AfterViewInit, ElementRef, Injector, NgZone } from '@angular/core';
import { Router } from '@angular/router';

import { filter, take } from 'rxjs/operators';

import { WebcomponentsService } from '@core/service/webcomponents.service';
import { AuthService } from '@auth/auth.service';
import {
  MESSAGE_ALGO_SALIO_MAL,
  show_popup,
  showPopupBase,
  TITLE_ERROR,
  TITLE_MENSAJE,
} from '@shared/utility/popup-error';
import { ROUTES_CONSTANTS } from '@shared/configs';

import { PRODUCT_APPLICATION_STATE_CONSTANT, ProductType, SCREEN_TYPE_CONSTANTS } from '@core/model/enums';
import { WebElementRefInstanceType } from '@model/web-element-ref-instance.type';

export abstract class RefinanceBaseComponent implements AfterViewInit {
  protected abstract get webElementRef(): ElementRef;
  protected abstract set webElementRefInstance(instance: WebElementRefInstanceType);
  protected abstract get productType(): ProductType;

  protected readonly ngZone: NgZone;

  private readonly webElementService: WebcomponentsService;
  private readonly router: Router;
  private readonly authService: AuthService;

  constructor(protected readonly injector: Injector) {
    this.webElementService = this.injector.get(WebcomponentsService);
    this.router = this.injector.get(Router);
    this.ngZone = this.injector.get(NgZone);
    this.authService = this.injector.get(AuthService);
  }

  public ngAfterViewInit(): void {
    this.loadWebElementRef();
  }

  public loadWebElementRef(): void {
    this.webElementService.isLoaded$.pipe(filter(Boolean), take(1)).subscribe({
      next: (loaded: boolean) => {
        if (loaded) {
          const token = this.authService?.token;
          const clientId = this.authService?.getTokenData(token).clienteId;
          const instance = this.webElementRef.nativeElement._ngElementStrategy?.componentRef.instance;
          this.webElementRefInstance = instance;
          instance.build({
            manageApplicationResponseFn: this.manageApplicationResponseFn.bind(this),
            showErrorPopUpFn: this.showErrorPopUpFn.bind(this),
            clientId,
          });
        }
      },
    });
  }

  public manageApplicationResponseFn(result): void {
    this.ngZone.run(() => {
      const state = result?.response?.state;
      const navigationByState = {
        [PRODUCT_APPLICATION_STATE_CONSTANT.RET_RP.ON_PROCESS]: () =>
          this.router.navigate([ROUTES_CONSTANTS.DISPERSION_METHOD]),
        [PRODUCT_APPLICATION_STATE_CONSTANT.RET_RP.DECLINED]: () =>
          this.router.navigate([ROUTES_CONSTANTS.REJECTED], {
            queryParams: { productType: this.productType, screenType: SCREEN_TYPE_CONSTANTS.REJECTED },
          }),
        [PRODUCT_APPLICATION_STATE_CONSTANT.RET_RP.CANCELED]: () =>
          this.router.navigate([ROUTES_CONSTANTS.APPLICATION_REJECTION], {
            queryParams: { productType: this.productType, screenType: SCREEN_TYPE_CONSTANTS.CANCELED },
          }),
      };

      const navigate = navigationByState[state];

      if (navigate) {
        navigate();
      } else if (result?.response?.navigableResponseDto?.message) {
        showPopupBase(TITLE_MENSAJE, result.response.navigableResponseDto.message).then(() => {
          this.router.navigate([ROUTES_CONSTANTS.APPLICATION_REJECTION]);
        });
      }
    });
  }

  public showErrorPopUpFn(): void {
    show_popup(TITLE_ERROR, MESSAGE_ALGO_SALIO_MAL);
  }
}
